/**
 * Dialog batch convocations — 1 lettre personnalisée par associé en 1 clic.
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Send, Users2, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function BatchConvocationsDialog({ open, onOpenChange, assemblyId }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sendEmails, setSendEmails] = useState(false);
  const [includeInactives, setIncludeInactives] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!date) { toast.error("Date d'envoi requise"); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post(`/legal/assemblies/${assemblyId}/convocations/batch`, {
        convocation_date: date,
        send_emails: sendEmails,
        include_inactives: includeInactives,
      });
      setResult(r.data);
      if (r.data.generated > 0) toast.success(`${r.data.generated} convocation(s) générée(s)`);
      else toast.info("Aucune convocation générée");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const close = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="w-5 h-5 text-purple-600" />
            Convocations groupées — 1 par associé
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              L'IA génère une lettre <strong>personnalisée</strong> par associé actif, en utilisant son adresse postale structurée.
            </p>
            <div>
              <Label>Date d'envoi de la convocation *</Label>
              <DateInputFR value={date} onChange={(e) => setDate(e.target.value)} data-testid="batch-conv-date" />
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="text-sm font-medium">Envoyer aussi par email</p>
                <p className="text-xs text-slate-500">Uniquement aux associés avec email renseigné.</p>
              </div>
              <Switch checked={sendEmails} onCheckedChange={setSendEmails} data-testid="batch-conv-send-emails" />
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="text-sm font-medium">Inclure les associés sortis</p>
                <p className="text-xs text-slate-500">Par défaut, seuls les associés actifs (date_sortie vide) sont convoqués.</p>
              </div>
              <Switch checked={includeInactives} onCheckedChange={setIncludeInactives} />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="batch-conv-result">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 bg-emerald-50 rounded border border-emerald-200 text-center">
                <p className="text-2xl font-bold text-emerald-700">{result.generated}</p>
                <p className="text-xs text-emerald-700 mt-1">Générées</p>
              </div>
              <div className="p-3 bg-blue-50 rounded border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-700">{result.emails_sent}</p>
                <p className="text-xs text-blue-700 mt-1">Emails envoyés</p>
              </div>
              <div className="p-3 bg-amber-50 rounded border border-amber-200 text-center">
                <p className="text-2xl font-bold text-amber-700">{result.skipped_no_address}</p>
                <p className="text-xs text-amber-700 mt-1">Sans adresse</p>
              </div>
            </div>

            {result.convocations?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Convocations générées</p>
                <div className="border rounded divide-y max-h-60 overflow-y-auto">
                  {result.convocations.map((c) => (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>{c.associe_name}</span>
                      </div>
                      {c.has_email
                        ? <Badge className="bg-blue-100 text-blue-700 text-xs">Email envoyé</Badge>
                        : <Badge variant="outline" className="text-xs">Papier</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="p-3 bg-red-50 rounded border border-red-200 text-xs text-red-700">
                <div className="flex items-center gap-1 font-semibold mb-1">
                  <AlertTriangle className="w-3 h-3" />
                  {result.errors.length} erreur(s)
                </div>
                <ul className="list-disc list-inside">
                  {result.errors.slice(0, 5).map((e, i) => <li key={i}>{e.error}</li>)}
                </ul>
              </div>
            )}

            <a
              href={`${api.defaults.baseURL}/legal/assemblies/${assemblyId}/convocations/batch/zip`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              data-testid="batch-conv-zip-link">
              <Download className="w-4 h-4" />Télécharger toutes les convocations (ZIP PDF)
            </a>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <Button onClick={run} disabled={loading || !date} data-testid="batch-conv-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Générer les convocations
            </Button>
          ) : (
            <Button onClick={close} variant="outline">Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
